import { useId } from "react";
import { money, moneyShort } from "@/plan-logic/lib/format";
import type { Point } from "@/plan-logic/plan-view";
import { CrosshairSvg, valueAt } from "./Crosshair";
import { RetirementMark } from "./RetirementMark";
import { niceAxis, tickText } from "./scale";

/**
 * The prototype's net-worth trajectory (`traj`, v47): the current trajectory (blue, solid) and
 * Abi's plan (gold), a shaded band between them, from today (the plan's first state; there is no
 * "today" label, the axis says it). With `retirement`, a dotted rule labelled with the year above
 * the plot, and each line's figure at retirement beside its dot in the line's colour; without it,
 * the figures sit at the lines' ends. A crosshair reads both lines at the pointer. Data comes from
 * plan-view's yearly series; nothing is projected here.
 */
export function TrajectoryChart({
  abi,
  naive,
  height = 300,
  label,
  before = "var(--before)",
  after = "var(--after)",
  todayLabel,
  retirement,
  names = { abi: "Abi’s plan", naive: "Your current path" },
}: {
  abi: Point[];
  naive?: Point[];
  height?: number;
  label?: string;
  before?: string;
  after?: string;
  /** Text above the starting point ("Today $533,903"). */
  todayLabel?: string;
  /** The household's retirement: where to draw the marker (a fractional year) and its year. */
  retirement?: { x: number; year: number };
  /** The lines' names in the crosshair's readout. */
  names?: { abi: string; naive: string };
}) {
  const gid = useId().replace(/:/g, "");
  if (abi.length < 2) return <p className="mut">Not enough of the plan to chart yet.</p>;
  const all = [...abi, ...(naive ?? [])];
  const x0 = Math.min(...all.map((p) => p.x));
  const x1 = Math.max(...all.map((p) => p.x));
  const ret = retirement && retirement.x > x0 && retirement.x <= x1 ? retirement : undefined;
  const W = 880;
  const H = ret ? height + 16 : height;
  const L = 58;
  const R = 76;
  const T = ret ? 46 : 18;
  const B = 30;
  const axis = niceAxis(Math.min(...all.map((p) => p.value)), Math.max(...all.map((p) => p.value)));
  const { lo, hi } = axis;
  const X = (x: number) => L + ((x - x0) / (x1 - x0 || 1)) * (W - L - R);
  const Y = (v: number) => H - B - ((v - lo) / (hi - lo || 1)) * (H - T - B);
  const path = (pts: Point[]) =>
    pts.map((p, i) => `${i ? "L" : "M"}${X(p.x).toFixed(1)} ${Y(p.value).toFixed(1)}`).join(" ");
  // The band spans only the years both lines cover: a plan that runs short stops early, and a band
  // to the other line's end would shade a wedge under nothing.
  const bandEnd = naive?.length ? Math.min(abi[abi.length - 1].x, naive[naive.length - 1].x) : 0;
  const bandA = upTo(abi, bandEnd);
  const bandN = naive ? upTo(naive, bandEnd) : [];
  const band =
    bandA.length > 1 && bandN.length > 1
      ? `${path(bandA)} ${[...bandN]
          .reverse()
          .map((p) => `L${X(p.x).toFixed(1)} ${Y(p.value).toFixed(1)}`)
          .join(" ")} Z`
      : "";
  const firstYear = Math.ceil(x0);
  const lastYear = Math.floor(x1);
  const span = lastYear - firstYear;
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(firstYear + f * span));

  // Each line's figure: at retirement when it's marked (where both lines peak), else at its end.
  const figure = (pts: Point[]) => {
    const last = pts[pts.length - 1];
    const v = ret ? valueAt(pts, ret.x) : undefined;
    return v === undefined ? { x: last.x, value: last.value } : { x: ret!.x, value: v };
  };
  const endA = figure(abi);
  const endB = naive && naive.length > 1 ? figure(naive) : undefined;
  const start = abi[0];
  // Beside the dot, in the line's colour (v44): the higher figure above its dot and the lower one
  // below, so neither sits on the other or on its own line as it falls away after retirement.
  const high = !endB || endA.value >= endB.value;
  const aY = Y(endA.value) + (high ? -9 : 19);
  const bY = endB ? Y(endB.value) + (high ? 19 : -9) : 0;

  // The crosshair samples every point of either line.
  const xs = [...new Set(all.map((p) => p.x))].sort((a, b) => a - b);
  const dateOf = new Map(all.map((p) => [p.x, p.date]));
  const labels = xs.map((x, i) =>
    i === 0 ? "Today" : String(dateOf.get(x)?.year ?? Math.floor(x)),
  );
  const series = [
    { n: names.abi, c: after, v: xs.map((x) => valueAt(abi, x)) },
    ...(naive && naive.length > 1
      ? [{ n: names.naive, c: before, v: xs.map((x) => valueAt(naive, x)) }]
      : []),
  ];
  const where = ret ? ` at retirement in ${ret.year}` : "";

  return (
    <div data-testid="trajectory-chart">
      <CrosshairSvg
        W={W}
        H={H}
        L={L}
        R={R}
        T={T}
        B={B}
        xs={xs.map(X)}
        labels={labels}
        series={series}
        y={Y}
        label={
          label ??
          `Net worth over the plan. On Abi’s plan, ${money(endA.value)}${where}${
            endB ? `. On your current trajectory, ${money(endB.value)}` : ""
          }.`
        }
      >
        {axis.ticks.map((t) => (
          <g key={`y${t}`}>
            <line x1={L} x2={W - R} y1={Y(t)} y2={Y(t)} stroke="var(--line2, var(--line-2))" />
            <text
              x={L - 9}
              y={Y(t) + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--ink3, var(--ink-3))"
            >
              {tickText(t, axis.step)}
            </text>
          </g>
        ))}
        {[...new Set(xTicks)].map((t) => (
          <text
            key={`x${t}`}
            x={X(t)}
            y={H - 9}
            textAnchor="middle"
            fontSize="11"
            fill="var(--ink3, var(--ink-3))"
          >
            {t}
          </text>
        ))}
        <defs>
          <linearGradient id={`traj${gid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={after} stopOpacity=".22" />
            <stop offset="1" stopColor={after} stopOpacity="0" />
          </linearGradient>
        </defs>
        {band && <path d={band} fill={`url(#traj${gid})`} />}
        {naive && naive.length > 1 && (
          <path
            d={path(naive)}
            fill="none"
            stroke={before}
            strokeWidth="2"
            strokeLinecap="round"
            data-testid="trajectory-naive"
          />
        )}
        <path
          d={path(abi)}
          fill="none"
          stroke={after}
          strokeWidth="2.5"
          strokeLinecap="round"
          data-testid="trajectory-abi"
        />
        {ret && <RetirementMark x={X(ret.x)} top={T} bottom={H - B} year={ret.year} />}
        <circle
          cx={X(start.x)}
          cy={Y(start.value)}
          r="5"
          fill="var(--save)"
          stroke="var(--card)"
          strokeWidth="2"
        />
        {todayLabel && (
          <text
            x={X(start.x)}
            y={Y(start.value) - 10}
            fontSize="11"
            fill="var(--ink3, var(--ink-3))"
          >
            {todayLabel}
          </text>
        )}
        <circle cx={X(endA.x)} cy={Y(endA.value)} r="4.5" fill={after} />
        <text
          x={(X(endA.x) + 10).toFixed(1)}
          y={aY.toFixed(1)}
          textAnchor="start"
          fontSize="12.5"
          fontWeight="700"
          fill={after}
          data-testid="trajectory-abi-figure"
        >
          {moneyShort(endA.value)}
        </text>
        {endB && (
          <>
            <circle cx={X(endB.x)} cy={Y(endB.value)} r="4.5" fill={before} />
            <text
              x={(X(endB.x) + 10).toFixed(1)}
              y={bY.toFixed(1)}
              textAnchor="start"
              fontSize="12.5"
              fontWeight="700"
              fill={before}
              data-testid="trajectory-naive-figure"
            >
              {moneyShort(endB.value)}
            </text>
          </>
        )}
      </CrosshairSvg>
    </div>
  );
}

export function TrajectoryLegend({ naive = true }: { naive?: boolean }) {
  return (
    <div className="legend trajleg" style={{ marginTop: 6 }}>
      <span>
        <i className="lgd" style={{ background: "var(--after)" }} />
        Abi’s plan
      </span>
      {naive && (
        <span>
          <i className="lgd" style={{ background: "var(--before)" }} />
          Your current trajectory
        </span>
      )}
    </div>
  );
}

/** The points up to `x`, ending with one interpolated at `x` when the line goes on past it. */
function upTo(pts: Point[], x: number): Point[] {
  const kept = pts.filter((p) => p.x <= x);
  const next = pts.find((p) => p.x > x);
  const last = kept[kept.length - 1];
  if (!next || !last || last.x === x) return kept;
  const t = (x - last.x) / (next.x - last.x);
  return [...kept, { ...last, x, value: last.value + t * (next.value - last.value) }];
}
